import { Box, Portal } from '@chronosphereio/chrono-ui';
import useResizeObserver from 'use-resize-observer';
import { MouseEventsParameters } from './EChart';

const VIEWPORT_PADDING = 8;
const POINTER_OFFSET_PX = 12;

export interface MarkLineTooltipData {
  clientX: number;
  clientY: number;
  description: string;
  name?: string;
}

/**
 * Custom data fields attached to ECharts threshold markLine items so they can
 * be read back from `params.data` in the click handler.
 *
 * Note: avoid using ECharts-reserved keys here (e.g. `name`, `value`).
 * ECharts uses `name` internally for animation matching during data updates,
 * so reusing it would cause unrelated thresholds to be matched together.
 */
export interface ThresholdMarkLineCustomData {
  description?: string;
  thresholdName?: string;
}

/**
 * If `params` is a click on a threshold markLine that carries a description,
 * returns the data needed to render the popover. Otherwise returns `null`.
 *
 * Centralizing this avoids duplicate type-narrowing logic across each chart
 * component that wants to support clickable thresholds.
 */
export function getMarkLineTooltipDataFromClick(params: MouseEventsParameters<unknown>): MarkLineTooltipData | null {
  if (params.componentType !== 'markLine') return null;
  const data = params.data as ThresholdMarkLineCustomData | undefined;
  if (!data?.description) return null;
  const nativeEvent = params.event?.event;
  if (!(nativeEvent instanceof MouseEvent)) return null;
  nativeEvent.stopPropagation();
  return {
    clientX: nativeEvent.clientX,
    clientY: nativeEvent.clientY,
    description: data.description,
    name: data.thresholdName,
  };
}

/**
 * Tooltip popover shown when clicking a threshold markLine symbol. Positioned
 * above the click point, falls back to below if it would clip the top edge,
 * and clamped horizontally to the viewport.
 */
export function MarkLineTooltip({ data, onClose }: { data: MarkLineTooltipData; onClose?: () => void }) {
  const { ref, width: tooltipWidth = 0, height: tooltipHeight = 0 } = useResizeObserver<HTMLDivElement>();

  let x = data.clientX - tooltipWidth / 2;
  let y = data.clientY - tooltipHeight - POINTER_OFFSET_PX;

  if (x < VIEWPORT_PADDING) {
    x = VIEWPORT_PADDING;
  } else if (x + tooltipWidth > window.innerWidth - VIEWPORT_PADDING) {
    x = window.innerWidth - VIEWPORT_PADDING - tooltipWidth;
  }
  if (y < VIEWPORT_PADDING) {
    y = data.clientY + POINTER_OFFSET_PX;
  }

  return (
    <Portal>
      <Box
        ref={ref}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        sx={(theme) => ({
          position: 'fixed',
          top: y,
          left: x,
          backgroundColor: theme.palette.background.popover,
          borderRadius: '4px',
          boxShadow: theme.shadows[5],
          padding: '8px 12px',
          zIndex: theme.zIndex.tooltip,
          maxWidth: 300,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
          color: theme.palette.text.primary,
          pointerEvents: 'auto',
        })}
      >
        {data.name && <Box sx={{ fontWeight: 'bold', marginBottom: '2px' }}>{data.name}</Box>}
        <div>{data.description}</div>
      </Box>
    </Portal>
  );
}
